import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faChevronRight, faChevronDown, faTag } from "@fortawesome/free-solid-svg-icons";
import { CampaignAsset, CampaignAssetSection } from "../../types/types";
import AssetMarkdownField from "../../pages/assets/explorer/AssetMarkdownField";
import { getTypeConfig } from "../../pages/assets/explorer/typeConfig";
import { AssetTableItem } from "../../types/types";

// ── Types ──────────────────────────────────────────────────────────────────────

export interface KnowledgeDetailPanelProps {
  asset: CampaignAsset;
  /** Full list of accessible campaign assets — used for reference chip navigation. */
  allAssets: CampaignAsset[];
  /** IDs of assets the character has access to (for reference chip discovery check). */
  accessibleIds: Set<number>;
  onSelectAsset: (_id: number) => void;
  /** compact = session panel (less whitespace, sections collapsed by default) */
  compact?: boolean;
}

// ── Helper — convert campaign assets to AssetTableItem for AssetMarkdownField ──

function campaignAssetsToTableItems(campaignAssets: CampaignAsset[]): AssetTableItem[] {
  return campaignAssets.map((ca) => ({ ...ca.asset, id: ca.id, children: undefined }));
}

// ── Component ─────────────────────────────────────────────────────────────────

export const KnowledgeDetailPanel: React.FC<KnowledgeDetailPanelProps> = ({
  asset,
  allAssets,
  accessibleIds,
  onSelectAsset,
  compact = false,
}) => {
  const cfg = getTypeConfig(asset.asset.type);
  const tags = Array.isArray(asset.asset.tags) ? asset.asset.tags : [];
  const sections: CampaignAssetSection[] = asset.sections ?? [];

  const [collapsedSections, setCollapsedSections] = useState<Set<number>>(
    compact ? new Set(sections.map((s) => s.id)) : new Set()
  );

  const toggleSection = (id: number) => {
    setCollapsedSections((prev) => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  const parentAsset = asset.parentId
    ? (allAssets.find((ca) => ca.id === asset.parentId) ?? null)
    : null;
  const childAssets = allAssets.filter((ca) => ca.parentId === asset.id);

  const assetItems = campaignAssetsToTableItems(allAssets);

  // Navigation for reference chips — only accessible assets are navigable.
  const handleReferenceNavigate = (refId: number) => {
    if (accessibleIds.has(refId)) {
      onSelectAsset(refId);
    }
    // Unknown assets: no-op (chip already visually greyed out by AssetMarkdownField readOnly)
  };

  return (
    <div className={compact ? "px-3 py-3" : "px-6 py-5"}>
      {/* Read-only badge */}
      <div className="flex items-center gap-1.5 mb-3 text-xs text-zinc-500">
        <FontAwesomeIcon icon={faEye} className="text-[10px]" />
        <span>Character view · read only</span>
      </div>

      {/* Asset header */}
      <div className={`flex items-start gap-4 ${compact ? "mb-4" : "mb-6"}`}>
        <div
          className={`${compact ? "w-9 h-9" : "w-14 h-14"} bg-zinc-800/80 border border-zinc-700 flex items-center justify-center rounded-lg shrink-0`}
        >
          <FontAwesomeIcon
            icon={cfg.icon}
            className={`${cfg.color} ${compact ? "text-base" : "text-2xl"}`}
          />
        </div>
        <div className="flex-1 min-w-0">
          <h2
            className={`${compact ? "text-base" : "text-2xl"} font-light text-white tracking-wide leading-tight mb-1`}
          >
            {asset.asset.name}
          </h2>
          <p className={`${compact ? "text-xs" : "text-sm"} text-zinc-400 mb-2`}>
            <span className={cfg.color}>{asset.asset.type}</span>
            <span className="text-zinc-600 mx-1.5">·</span>
            <span>{asset.asset.category}</span>
          </p>
          {tags.length > 0 && (
            <div className="flex gap-1.5 flex-wrap items-center">
              <FontAwesomeIcon icon={faTag} className="text-zinc-600 text-xs" />
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-amber-400/10 text-amber-300 border border-amber-400/25"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Description */}
      {asset.asset.description && (
        <div className={compact ? "mb-4" : "mb-6"}>
          <AssetMarkdownField
            value={asset.asset.description}
            label="DESCRIPTION"
            allAssets={assetItems}
            onReferenceNavigate={handleReferenceNavigate}
            readOnly
          />
        </div>
      )}

      {/* Sections */}
      {sections.length > 0 && (
        <div className={compact ? "mb-4" : "mb-6"}>
          <div className="flex items-center justify-between mb-3">
            <h3
              className={`${compact ? "text-[10px]" : "text-xs"} font-semibold text-amber-400 tracking-widest uppercase`}
            >
              Sections
            </h3>
          </div>
          <div className="space-y-2">
            {sections.map((sec) => {
              const isCollapsed = collapsedSections.has(sec.id);
              return (
                <div
                  key={sec.id}
                  className="border border-zinc-800 bg-zinc-900/40 hover:border-zinc-700 transition-colors rounded-sm"
                >
                  {/* Section header */}
                  <div
                    className="flex items-center gap-2 px-3 py-2 cursor-pointer"
                    onClick={() => toggleSection(sec.id)}
                  >
                    <FontAwesomeIcon
                      icon={isCollapsed ? faChevronRight : faChevronDown}
                      className="text-zinc-500 text-xs shrink-0"
                    />
                    <span
                      className={`${compact ? "text-xs" : "text-sm"} font-medium text-zinc-200 flex-1`}
                    >
                      {sec.name}
                    </span>
                  </div>
                  {/* Section content — read only */}
                  {!isCollapsed && sec.content && (
                    <div className="px-3 pb-3 border-t border-zinc-800 pt-2">
                      <AssetMarkdownField
                        value={sec.content}
                        allAssets={assetItems}
                        onReferenceNavigate={handleReferenceNavigate}
                        readOnly
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Hierarchy — only show accessible parent/children */}
      {!compact && (parentAsset !== null || childAssets.length > 0) && (
        <div className="border-t border-zinc-800 pt-4">
          <h3 className="text-xs font-semibold text-amber-400 tracking-widest uppercase mb-3">
            Hierarchy
          </h3>
          {parentAsset && (
            <>
              <p className="text-xs text-zinc-500 mb-2">Parent</p>
              <button
                className="inline-flex items-center gap-2 px-3 py-1.5 text-xs bg-zinc-800 border border-zinc-700 text-zinc-300 hover:border-zinc-500 transition-colors mb-3 rounded-sm"
                onClick={() => onSelectAsset(parentAsset.id)}
              >
                <FontAwesomeIcon
                  icon={getTypeConfig(parentAsset.asset.type).icon}
                  className={getTypeConfig(parentAsset.asset.type).color + " text-xs"}
                />
                {parentAsset.asset.name}
              </button>
            </>
          )}
          {childAssets.length > 0 && (
            <>
              <p className="text-xs text-zinc-500 mb-2">Children ({childAssets.length})</p>
              <div className="flex flex-wrap gap-2">
                {childAssets.map((child) => {
                  const childCfg = getTypeConfig(child.asset.type);
                  return (
                    <button
                      key={child.id}
                      className="inline-flex items-center gap-2 px-3 py-1.5 text-xs bg-zinc-800 border border-zinc-700 text-zinc-300 hover:border-zinc-500 transition-colors rounded-sm"
                      onClick={() => onSelectAsset(child.id)}
                    >
                      <FontAwesomeIcon
                        icon={childCfg.icon}
                        className={childCfg.color + " text-xs"}
                      />
                      {child.asset.name}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default KnowledgeDetailPanel;
